/*
 * gift-box-sync.js
 *
 * Charlie 2026-06-01:
 * Keeps the free Signature Gift Box line in sync with the total quantity of
 * UNIVERSAL LIVE PRODUCT (CHUUG) line items in the cart, GLOBALLY (not just
 * on the cart page). Runs after every cart mutation (add / change / update /
 * clear) and on DOMContentLoaded.
 *
 * Why: previously gift boxes were added only when the customer clicked the
 * "Secure Checkout" button. Express checkout buttons (Shop Pay, Apple Pay,
 * PayPal, Google Pay) bypass that JS, so orders shipped without their gift
 * boxes — bad UX, support tickets, refunds.
 *
 * Strategy:
 *   - Liquid bakes the list of qualifying variant IDs into the page as
 *     window.CHUUG_LIVE_VARIANT_IDS (array of ints).
 *   - Liquid bakes the giftbox variant ID into window.CHUUG_GIFTBOX_VARIANT_ID.
 *   - We monkey-patch window.fetch (alongside the topbar's existing patch) so
 *     any /cart/{add,change,update,clear}.js triggers a debounced sync.
 *   - We also intercept classic non-AJAX form posts to /cart/add (used by
 *     the PDP) so we can sync BEFORE the page navigates away.
 *
 * Sync logic:
 *   target  = sum of qty for cart lines whose variant_id ∈ CHUUG_LIVE_VARIANT_IDS
 *   current = qty of CHUUG_GIFTBOX_VARIANT_ID lines (sum across any duplicates)
 *   if target > current → POST /cart/add.js to add the diff
 *   if target < current → POST /cart/change.js to reduce to target (or remove)
 *   if target = current → no-op
 *
 * All giftbox lines carry properties: { "PERSONALISED COIN": "NONE" }
 * so the downstream fulfilment tagger doesn't flag the order Invalid_Name.
 *
 * Defensive: every fetch is try/catch'd; a sync failure logs and continues
 * so it can never block checkout.
 */
(function(){
  'use strict';

  // ── Config from Liquid ────────────────────────────────────────────────
  var LIVE_IDS = (window.CHUUG_LIVE_VARIANT_IDS || []).map(function(x){ return parseInt(x, 10); }).filter(Boolean);
  var GIFTBOX_ID = parseInt(window.CHUUG_GIFTBOX_VARIANT_ID, 10);

  if (!GIFTBOX_ID) {
    console.warn('[gift-sync] no giftbox variant ID configured — aborting');
    return;
  }
  if (!LIVE_IDS.length) {
    console.warn('[gift-sync] no live variant IDs configured — aborting');
    return;
  }

  // Fast lookup for "is this variant a qualifying live product?"
  var LIVE_SET = Object.create(null);
  LIVE_IDS.forEach(function(id){ LIVE_SET[id] = true; });

  // ── State ─────────────────────────────────────────────────────────────
  var syncing = false;        // single-flight lock
  var syncQueued = false;     // remember if a sync was requested mid-sync
  var debounceTimer = null;
  var DEBOUNCE_MS = 60;       // coalesce rapid mutations
  var inflightInternal = 0;   // count our own cart fetches so we don't recurse

  // Expose for debugging / manual trigger.
  //
  // beginExternalSwap() / endExternalSwap() let other cart code (e.g.
  // the personalisation-property × handler in main-cart.liquid) declare
  // "I'm running a multi-step cart mutation; please do not auto-sync
  // gift boxes between my steps and please do not reload the page."
  // Without this, gift-box-sync sees the intermediate "remove the only
  // CHUUG line" state, decides there are 0 CHUUGs, removes the gift
  // box too, and triggers its own location.reload() — racing the
  // external code's add.js follow-up. Charlie 2026-06-02.
  window.CHUUG_GIFTBOX_SYNC = {
    sync: scheduleSync,
    config: { liveIds: LIVE_IDS, giftboxId: GIFTBOX_ID },
    beginExternalSwap: function(){ inflightInternal++; },
    endExternalSwap:   function(){ inflightInternal = Math.max(0, inflightInternal - 1); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────
  function isInternalFetch(){ return inflightInternal > 0; }

  function fetchJSON(url, init){
    inflightInternal++;
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { 'Accept': 'application/json' } }, init || {}))
      .then(function(r){
        if (!r.ok) throw new Error('HTTP ' + r.status + ' on ' + url);
        return r.json();
      })
      .finally(function(){ inflightInternal--; });
  }

  function postJSON(url, body){
    return fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function dispatchCartUpdated(){
    try { document.dispatchEvent(new Event('cart:updated')); } catch(e){}
  }

  function countTargets(cart){
    var targetQty = 0;
    var giftboxQty = 0;
    var giftboxLineKey = null;
    var giftboxLineIdx = 0;
    (cart.items || []).forEach(function(it, i){
      var vid = it.variant_id;
      if (LIVE_SET[vid]) targetQty += it.quantity;
      if (vid === GIFTBOX_ID) {
        giftboxQty += it.quantity;
        if (!giftboxLineKey) {
          giftboxLineKey = it.key;       // preferred — survives line index shifts
          giftboxLineIdx = i + 1;        // fallback
        }
      }
    });
    return {
      target: targetQty,
      current: giftboxQty,
      lineKey: giftboxLineKey,
      lineIdx: giftboxLineIdx
    };
  }

  // ── The actual sync ───────────────────────────────────────────────────
  function doSync(){
    if (syncing) { syncQueued = true; return Promise.resolve(); }
    syncing = true;
    syncQueued = false;

    return fetchJSON('/cart.js')
      .then(function(cart){
        var s = countTargets(cart);

        if (s.target === s.current) {
          // Already in sync
          return null;
        }

        if (s.target > s.current) {
          var toAdd = s.target - s.current;
          console.info('[gift-sync] adjusting gift boxes from ' + s.current + ' to ' + s.target + ' (adding ' + toAdd + ')');
          return postJSON('/cart/add.js', {
            id: GIFTBOX_ID,
            quantity: toAdd,
            properties: { 'PERSONALISED COIN': 'NONE' }
          });
        }

        // target < current → reduce or remove
        console.info('[gift-sync] adjusting gift boxes from ' + s.current + ' to ' + s.target + ' (removing ' + (s.current - s.target) + ')');
        var body = { quantity: s.target };
        if (s.lineKey) body.id = s.lineKey;   // /cart/change.js accepts id=key
        else body.line = s.lineIdx;
        return postJSON('/cart/change.js', body);
      })
      .then(function(result){
        if (result) {
          dispatchCartUpdated();
          // If we mutated the cart while on the cart page, server-rendered
          // bits (giftbox banner count, line item HTML) are stale. Reload
          // so the banner shows the right "× N gift boxes included" count.
          // Guard against an infinite reload loop with sessionStorage.
          if (/^\/cart(\b|\/|$)/.test(window.location.pathname || '')) {
            try {
              var k = 'chuug-gift-sync-reloaded';
              if (!window.sessionStorage.getItem(k)) {
                window.sessionStorage.setItem(k, '1');
                setTimeout(function(){ window.location.reload(); }, 50);
              }
            } catch(e){
              setTimeout(function(){ window.location.reload(); }, 50);
            }
          }
        } else if (/^\/cart(\b|\/|$)/.test(window.location.pathname || '')) {
          // No mutation → clear the reload-guard so a future mutation
          // can reload again.
          try { window.sessionStorage.removeItem('chuug-gift-sync-reloaded'); } catch(e){}
        }
        return result;
      })
      .catch(function(err){
        console.warn('[gift-sync] sync failed:', err);
      })
      .finally(function(){
        syncing = false;
        if (syncQueued) setTimeout(scheduleSync, 0);
      });
  }

  function scheduleSync(){
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doSync, DEBOUNCE_MS);
  }

  // Expose for callers that need to AWAIT the sync (e.g. PDP form submit)
  function syncNow(){
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    return doSync().then(function(){
      // If another sync got queued while we ran, drain it too
      if (syncQueued) return doSync();
    });
  }
  window.CHUUG_GIFTBOX_SYNC.syncNow = syncNow;

  // ── Monkey-patch fetch to catch any cart mutation ─────────────────────
  // The topbar already has its own monkey-patch; ours coexists fine —
  // both layers wrap the original and both run their own logic.
  (function(){
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    var RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    window.fetch = function(input, init){
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch(e){}
      var p = origFetch.apply(this, arguments);
      if (!isInternalFetch() && url && RE.test(url)){
        p.then(function(){ scheduleSync(); }, function(){});
      }
      return p;
    };
  })();

  // ── Intercept classic <form action="/cart/add"> submits ───────────────
  // These bypass fetch entirely and navigate the page away — so we have to
  // intercept, POST via fetch ourselves, sync, then perform the redirect.
  // Skip forms that already use AJAX (look for a data-no-intercept hint).
  document.addEventListener('submit', function(e){
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.dataset.giftSyncIntercept === 'skip') return;
    var action = (form.getAttribute('action') || '').toLowerCase();
    if (!/\/cart\/add(\?|$|\.js)/.test(action)) return;
    // Only intercept if not already handled by something else (e.g. has
    // an onsubmit that returns false, or is being submitted via JS).
    if (form.dataset.giftSyncHandled === '1') return;

    e.preventDefault();
    form.dataset.giftSyncHandled = '1';

    var fd = new FormData(form);
    // /cart/add accepts form-urlencoded or multipart — FormData works.
    inflightInternal++;
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', body: fd })
      .then(function(r){ inflightInternal--; if (!r.ok) throw r; return r.json(); })
      .then(function(){
        return syncNow();
      })
      .then(function(){
        // Navigate to cart (or wherever the form intended). If the form has
        // a `return_to` input we honour it; otherwise default to /cart.
        var rt = form.querySelector('input[name="return_to"]');
        var dest = (rt && rt.value) || '/cart';
        window.location.href = dest;
      })
      .catch(function(err){
        inflightInternal = Math.max(0, inflightInternal - 1);
        console.warn('[gift-sync] PDP form intercept failed, falling back to native submit:', err);
        // Reset flag so native submit isn't blocked
        form.dataset.giftSyncHandled = '';
        // Fire native submit; rely on cart page sync as the safety net
        form.submit();
      });
  }, true); // capture phase so we beat any other submit listeners

  // ── On page load, sync once ───────────────────────────────────────────
  // Catches "customer returned from elsewhere, cart is stale" + the
  // critical "cart page just rendered, express checkout button is sitting
  // right there" case.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
})();
